import { ComponentType, lazy } from 'react';

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;
const RELOAD_ONCE_KEY = 'lazyWithRetry:reloaded';

const CHUNK_LOAD_ERROR_PATTERNS = [
  /importing a module script failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
];

function isChunkLoadError(error: unknown): boolean {
  return (
    error instanceof Error &&
    CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(error.message))
  );
}

function reloadOnceToRecoverStaleChunk() {
  if (window.sessionStorage.getItem(RELOAD_ONCE_KEY)) return;
  window.sessionStorage.setItem(RELOAD_ONCE_KEY, '1');
  window.location.reload();
}

export async function importWithRetry<T>(
  factory: () => Promise<T>,
  attempt = 0,
): Promise<T> {
  try {
    const module = await factory();
    window.sessionStorage.removeItem(RELOAD_ONCE_KEY);
    return module;
  } catch (error) {
    if (!isChunkLoadError(error)) {
      throw error;
    }
    if (attempt < MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      return importWithRetry(factory, attempt + 1);
    }
    reloadOnceToRecoverStaleChunk();
    throw error;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() => importWithRetry(factory));
}
